/**
 * Image optimization utilities for product images from Akeneo
 * Converts URLs to use auto format (AVIF > WebP > fallback)
 * Matches Prismic image optimization behavior
 */

/**
 * Optimizes product image URL for modern formats.
 * Uses format=auto to serve AVIF, WebP, or fallback based on browser support.
 *
 * @param url - The original image URL
 * @param dimensions - Optional width/height to inject. Only applied when the URL has no
 *   existing width (i.e. urlOriginal format). Ignored for URLs that already carry a width
 *   (e.g. urlThumbnail with /width=X,quality=Y/) to avoid overriding baked-in dimensions.
 * @returns Optimized URL with quality=85 and format=auto
 */
export const optimizeImageUrl = (
  url: string | null | undefined,
  dimensions?: { width?: number; height?: number }
): string => {
  if (!url) return "";

  const widthSegment = dimensions?.width ? `width=${dimensions.width},` : "";
  const heightSegment = dimensions?.height ? `height=${dimensions.height},` : "";

  // URLs with existing width+quality: /width=X,quality=100/ — dimensions not applied, width already baked in
  if (url.includes(",quality=")) {
    return url.replace(/,quality=\d+\//, ",quality=85,format=auto/");
  }

  // URLs with quality only: /quality=100/ — inject dimensions if provided
  if (url.includes("/quality=")) {
    return url.replace(
      /\/quality=\d+\//,
      `/${widthSegment}${heightSegment}quality=85,format=auto/`
    );
  }

  // URLs with width but no quality: /width=500/
  if (url.includes("/width=")) {
    return url.replace(
      /\/width=(\d+)\//,
      `/width=$1,${heightSegment}quality=85,format=auto/`
    );
  }

  return url;
};
